import React from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { baseURL } from '../../api/baseURL';
import AdminShell from '../layout/AdminShell';
import Loader from '../../Loader';
import Modal from '../../Modal';
import { MDBBtn } from 'mdb-react-ui-kit';
import { pillStyle, thStyle } from '../layout/listUi';
import { exportElementToPdf } from '../../utils/exportPagePdf';

type TrendPoint = { date: string; count?: number; gross?: number; net?: number };
type ZoneRow = { zone: string; revenue: number };
type MixRow = { type: string; label: string; revenue: number };
type GrowthMonth = { month: string; label: string; new: number; returning: number };
type TopEarner = { id: string; name: string; zone: string; bookings30d: number; earnings30d: number; rating: number | null };

type AnalyticsData = {
    range: number;
    bookingsTrend: TrendPoint[];
    revenueTrend: TrendPoint[];
    revenueByZone: ZoneRow[];
    revenueMix: MixRow[];
    customerGrowth: GrowthMonth[];
    avgOrderValue: number;
    repeatBookingRatePercent: number;
    photographerPerformance: {
        activeLast30: number;
        newThisMonth: number;
        churnRatePercent: number;
        topEarners: TopEarner[];
    };
};

type AppDownloadPoint = { date: string; downloads: number | null };
type AppDownloadPlatform = { configured: boolean; customer: AppDownloadPoint[]; photographer: AppDownloadPoint[]; allTimeTotal: { customer: number; photographer: number } };
type AppDownloadStats = { ios: AppDownloadPlatform; android: AppDownloadPlatform };

type State = {
    requested: boolean;
    err: boolean;
    range: number;
    data: AnalyticsData | null;
    exporting: boolean;
    appStats: AppDownloadStats | null;
    appStatsRefreshing: boolean;
};

const RANGE_OPTIONS = [7, 30, 90];
const PIE_COLORS = ['#0090e7', '#FFB020', '#36C5A1', '#FF6B6B', '#8C6BFF', '#FF9F43', '#54B4D3', '#EA5AA0'];
const ACCENT = '#0090e7';
const ACCENT_2 = '#FFB020';

function fmtDateLabel(dateStr: string): string {
    return moment(dateStr, 'YYYY-MM-DD').format('D MMM');
}

function fmtINR(v: number): string {
    const abs = Math.abs(v);
    if (abs >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (abs >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    if (abs >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return `₹${Math.round(v)}`;
}

function fmtNum(v: number): string {
    return Math.round(v).toLocaleString('en-IN');
}

const statTileStyle: React.CSSProperties = {
    flex: '1 1 140px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '16px 18px',
    textAlign: 'center',
};
const statNumStyle: React.CSSProperties = { fontSize: 24, fontWeight: 800 };
const statLblStyle: React.CSSProperties = { fontSize: 11.5, opacity: 0.6, marginTop: 4 };
const legendDotStyle = (color: string): React.CSSProperties => ({
    display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: color, marginRight: 6,
});
const cardTitleRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 };
const crumbStyle: React.CSSProperties = { fontSize: 11.5, opacity: 0.55, fontWeight: 500 };

type TrendSeries = { name: string; color: string; values: number[] };

/** Line chart matching the reference design's trend charts: horizontal grid
 * with value labels on the left, ~6 date labels along the bottom, an optional
 * soft area fill under the first series, and a hover guide with a tooltip.
 * Drawn in a fixed 1000-wide viewBox and scaled to the card's width, so any
 * number of days fits with no horizontal scrolling. */
function TrendChart({ series, labels, yFormat, area }: { series: TrendSeries[]; labels: string[]; yFormat: (v: number) => string; area?: boolean }) {
    const [hover, setHover] = React.useState<number | null>(null);
    const svgRef = React.useRef<SVGSVGElement>(null);

    const W = 1000, H = 300, padL = 64, padR = 16, padT = 16, padB = 32;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const n = labels.length;
    const allValues = series.flatMap(s => s.values);
    const yMax = (Math.max(0, ...allValues) * 1.15) || 10;
    const xAt = (i: number) => (n > 1 ? padL + (i / (n - 1)) * plotW : padL + plotW / 2);
    const yAt = (v: number) => padT + plotH - (v / yMax) * plotH;
    const pathFor = (values: number[]) => values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');
    const labelCount = Math.min(6, n);
    const labelIdx = Array.from({ length: labelCount }, (_, li) => Math.round(li * (n - 1) / Math.max(1, labelCount - 1)));

    const onMove = (e: React.MouseEvent) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect || !rect.width || n === 0) return;
        const relX = ((e.clientX - rect.left) / rect.width) * W;
        const idx = Math.round(((relX - padL) / plotW) * (n - 1));
        setHover(Math.max(0, Math.min(n - 1, idx)));
    };

    return (
        <div style={{ position: 'relative' }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
                {[0, 1, 2, 3, 4].map(g => {
                    const gy = padT + plotH - (g / 4) * plotH;
                    return (
                        <g key={g}>
                            <line x1={padL} y1={gy} x2={W - padR} y2={gy} stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
                            <text x={padL - 10} y={gy + 5} fontSize={17} textAnchor="end" fill="rgba(255,255,255,0.5)">{yFormat(Math.round(yMax * (g / 4)))}</text>
                        </g>
                    );
                })}
                {area && series[0] ? (
                    <path d={`${pathFor(series[0].values)} L${xAt(n - 1).toFixed(1)},${padT + plotH} L${xAt(0).toFixed(1)},${padT + plotH} Z`} fill={series[0].color} opacity={0.14} />
                ) : null}
                {series.map(s => (
                    <path key={s.name} d={pathFor(s.values)} fill="none" stroke={s.color} strokeWidth={2.5} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                ))}
                {labelIdx.map(i => (
                    <text key={i} x={xAt(i)} y={H - 8} fontSize={17} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fill="rgba(255,255,255,0.5)">{labels[i]}</text>
                ))}
                {hover != null ? (
                    <>
                        <line x1={xAt(hover)} y1={padT} x2={xAt(hover)} y2={padT + plotH} stroke="rgba(255,255,255,0.4)" strokeWidth={1} strokeDasharray="4 4" />
                        {series.map(s => <circle key={s.name} cx={xAt(hover)} cy={yAt(s.values[hover])} r={5} fill={s.color} stroke="#191c24" strokeWidth={2} />)}
                    </>
                ) : null}
            </svg>
            {hover != null ? (
                <div style={{
                    position: 'absolute', top: 4, left: `${(xAt(hover) / W) * 100}%`, transform: `translateX(${hover > n * 0.7 ? '-100%' : '0'})`,
                    background: '#0b0d12', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '7px 11px', fontSize: 11.5,
                    fontWeight: 700, whiteSpace: 'nowrap', pointerEvents: 'none', lineHeight: 1.6, zIndex: 5,
                }}>
                    <div style={{ opacity: 0.7, marginBottom: 2 }}>{labels[hover]}</div>
                    {series.map(s => (
                        <div key={s.name}><span style={legendDotStyle(s.color)} />{s.name}: {yFormat(s.values[hover])}</div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

/** Two bars per month (returning, new) with the count above each — the reference
 * design's customer-growth chart. Sized in % of the card so it never scrolls. */
function GrowthBars({ months }: { months: GrowthMonth[] }) {
    const max = Math.max(1, ...months.flatMap(m => [m.new, m.returning]));
    const barH = (v: number) => Math.round((v / max) * 110);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: 170, width: '100%', gap: 6 }}>
            {months.map(m => (
                <div key={m.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 0', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 132 }}>
                        {[{ v: m.returning, c: ACCENT, t: 'Returning' }, { v: m.new, c: ACCENT_2, t: 'New' }].map(b => (
                            <div key={b.t} title={`${m.label} · ${b.t}: ${b.v}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <span style={{ fontSize: 10.5, opacity: 0.7, marginBottom: 2 }}>{b.v}</span>
                                <div style={{ width: 16, maxWidth: '100%', height: b.v ? Math.max(3, barH(b.v)) : 2, background: b.c, borderRadius: '3px 3px 0 0', opacity: b.v ? 1 : 0.25 }} />
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>{m.label}</div>
                </div>
            ))}
        </div>
    );
}

class AnalyticsPage extends React.Component<{ token: string; role?: string }, State> {
    state: State = {
        requested: false,
        err: false,
        range: 7,
        data: null,
        exporting: false,
        appStats: null,
        appStatsRefreshing: false,
    };

    exportRef = React.createRef<HTMLDivElement>();

    // Screenshot/PDF-style export of exactly what is on screen, so the selected
    // range is what ends up in the file.
    exportPdf = async () => {
        if (!this.exportRef.current || this.state.exporting) return;
        this.setState({ exporting: true });
        try {
            await exportElementToPdf(this.exportRef.current, `analytics-last-${this.state.range}-days`);
        } catch (e) {
            this.setState({ err: true });
        } finally {
            this.setState({ exporting: false });
        }
    };

    componentDidMount(): void {
        this.fetchData();
        this.fetchAppStats();
    }

    // App Store/Play Store install counts (client-reported: "check whether
    // Play Store and App Store download statistics can be added") — a fixed
    // 30-day window, independent of the booking-data range toggle above,
    // since these numbers have nothing to do with orders/revenue.
    fetchAppStats = () => {
        axios.get(`${baseURL}/app-download-stats?days=30`, {
            headers: { Authorization: `Bearer ${this.props.token}` },
        })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ appStats: response.data });
                }
            })
            .catch(() => {});
    };

    refreshAppStats = () => {
        if (this.state.appStatsRefreshing) return;
        this.setState({ appStatsRefreshing: true });
        axios.post(`${baseURL}/refresh-app-download-stats`, {}, {
            headers: { Authorization: `Bearer ${this.props.token}` },
        })
            .then(() => this.fetchAppStats())
            .catch(() => {})
            .finally(() => this.setState({ appStatsRefreshing: false }));
    };

    fetchData = () => {
        this.setState({ requested: true, err: false });
        axios.get(`${baseURL}/analytics-data?range=${this.state.range}`, {
            headers: { Authorization: `Bearer ${this.props.token}` },
        })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ data: response.data, requested: false, err: false });
                } else {
                    this.setState({ err: true, requested: false });
                }
            })
            .catch(() => this.setState({ err: true, requested: false }));
    };

    setRange = (range: number) => {
        this.setState({ range }, this.fetchData);
    };

    toggleModal = () => {
        if (this.state.err) this.setState({ err: false });
    };

    render() {
        const { data, requested, err, range } = this.state;

        return (
            <AdminShell
                activePage="analytics"
                title="Analytics"
                subtitle="Revenue, growth & platform performance"
                topbarActions={
                    <MDBBtn
                        color='secondary'
                        rippleColor='light'
                        disabled={this.state.exporting || !data}
                        onClick={this.exportPdf}
                        style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        {this.state.exporting ? 'Exporting…' : 'Export as PDF'}
                    </MDBBtn>
                }
            >
                <Modal open={err} hideSec primaryText="Okay" title="Update" onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>Something went wrong, please try again later</h6>} />
                {requested ? <Loader /> : null}

                <div ref={this.exportRef} style={{ background: '#000' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {RANGE_OPTIONS.map(r => (
                        <div key={r} style={{ ...pillStyle(range === r), padding: '7px 16px', fontSize: 12.5 }} onClick={() => this.setRange(r)}>
                            Last {r} days
                        </div>
                    ))}
                </div>

                {!data ? null : (() => {
                    const labels = data.revenueTrend.map(p => fmtDateLabel(p.date));
                    const totalBookings = data.bookingsTrend.reduce((t, p) => t + (p.count || 0), 0);
                    const totalGross = data.revenueTrend.reduce((t, p) => t + (p.gross || 0), 0);
                    const totalNet = data.revenueTrend.reduce((t, p) => t + (p.net || 0), 0);
                    const zoneMax = data.revenueByZone.reduce((m, z) => Math.max(m, z.revenue), 0) || 1;
                    const mixTotal = data.revenueMix.reduce((s, m) => s + m.revenue, 0);
                    return (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                            <div className="card">
                                <div className="card-body">
                                    <div style={cardTitleRowStyle}>
                                        <h4 className="card-title" style={{ marginBottom: 0 }}>Revenue trend</h4>
                                        <span style={crumbStyle}>Gross {fmtINR(totalGross)} · Net {fmtINR(totalNet)}</span>
                                    </div>
                                    <div style={{ fontSize: 11.5, marginBottom: 8 }}>
                                        <span style={legendDotStyle(ACCENT)} />Net revenue&nbsp;&nbsp;
                                        <span style={legendDotStyle(ACCENT_2)} />Gross revenue
                                    </div>
                                    <TrendChart
                                        labels={labels}
                                        yFormat={fmtINR}
                                        series={[
                                            { name: 'Net', color: ACCENT, values: data.revenueTrend.map(p => p.net || 0) },
                                            { name: 'Gross', color: ACCENT_2, values: data.revenueTrend.map(p => p.gross || 0) },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-body">
                                    <div style={cardTitleRowStyle}>
                                        <h4 className="card-title" style={{ marginBottom: 0 }}>Bookings trend</h4>
                                        <span style={crumbStyle}>{fmtNum(totalBookings)} bookings</span>
                                    </div>
                                    <div style={{ fontSize: 11.5, marginBottom: 8 }}>
                                        <span style={legendDotStyle(ACCENT)} />Total bookings
                                    </div>
                                    <TrendChart
                                        labels={labels}
                                        yFormat={fmtNum}
                                        area
                                        series={[{ name: 'Bookings', color: ACCENT, values: data.bookingsTrend.map(p => p.count || 0) }]}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: 16, marginBottom: 16 }}>
                            <div className="card">
                                <div className="card-body">
                                    <div style={cardTitleRowStyle}>
                                        <h4 className="card-title" style={{ marginBottom: 0 }}>Revenue by zone</h4>
                                        <span style={crumbStyle}>selected range</span>
                                    </div>
                                    {data.revenueByZone.length === 0 ? <p style={{ fontSize: 13, opacity: 0.6 }}>No bookings in this range.</p> : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {data.revenueByZone.map(z => (
                                                <div key={z.zone} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ width: 110, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={z.zone}>{z.zone}</span>
                                                    <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 12 }}>
                                                        <div style={{ width: `${(z.revenue / zoneMax) * 100}%`, background: z.zone === 'Other' ? '#6b7280' : ACCENT, height: '100%', borderRadius: 4 }} />
                                                    </div>
                                                    <span style={{ width: 64, fontSize: 12.5, textAlign: 'right' }}>{fmtINR(z.revenue)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-body">
                                    <div style={cardTitleRowStyle}>
                                        <h4 className="card-title" style={{ marginBottom: 0 }}>Revenue mix</h4>
                                        <span style={crumbStyle}>by shoot type</span>
                                    </div>
                                    {data.revenueMix.length === 0 ? <p style={{ fontSize: 13, opacity: 0.6 }}>No bookings in this range.</p> : (() => {
                                        const total = mixTotal || 1;
                                        let acc = 0;
                                        const stops = data.revenueMix.map((m, i) => {
                                            const start = (acc / total) * 360;
                                            acc += m.revenue;
                                            const end = (acc / total) * 360;
                                            return `${PIE_COLORS[i % PIE_COLORS.length]} ${start}deg ${end}deg`;
                                        }).join(', ');
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                                                <div style={{
                                                    width: 120, height: 120, borderRadius: '50%',
                                                    background: `conic-gradient(${stops})`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                }}>
                                                    <div style={{ width: 66, height: 66, borderRadius: '50%', background: '#1a1d29', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                        <b style={{ fontSize: 13 }}>{fmtINR(mixTotal)}</b>
                                                        <span style={{ fontSize: 9, opacity: 0.5 }}>revenue</span>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: 1 }}>
                                                    {data.revenueMix.map((m, i) => (
                                                        <div key={m.type} style={{ fontSize: 12 }}>
                                                            <span style={legendDotStyle(PIE_COLORS[i % PIE_COLORS.length])} />
                                                            {m.label} — {Math.round((m.revenue / total) * 100)}% · {fmtINR(m.revenue)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                    {this.state.appStats ? (() => {
                        const { ios, android } = this.state.appStats!;
                        const platforms: { key: 'ios' | 'android'; label: string; data: AppDownloadPlatform }[] = [
                            { key: 'ios', label: 'App Store', data: ios },
                            { key: 'android', label: 'Play Store', data: android },
                        ];
                        return (
                            <div className="card" style={{ marginBottom: 16 }}>
                                <div className="card-body">
                                    <div style={cardTitleRowStyle}>
                                        <h4 className="card-title" style={{ marginBottom: 0 }}>App Downloads</h4>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={crumbStyle}>last 30 days · updates once a day</span>
                                            <MDBBtn size="sm" color="secondary" disabled={this.state.appStatsRefreshing} onClick={this.refreshAppStats}>
                                                {this.state.appStatsRefreshing ? 'Refreshing…' : 'Refresh'}
                                            </MDBBtn>
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        {platforms.map(p => !p.data.configured ? (
                                            <div key={p.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160, border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 10 }}>
                                                <span style={{ fontSize: 12.5, opacity: 0.55 }}>{p.label} — not connected yet</span>
                                            </div>
                                        ) : (
                                            <div key={p.key}>
                                                <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.7, marginBottom: 8 }}>{p.label}</div>
                                                {(['customer', 'photographer'] as const).map(appKey => {
                                                    const points = p.data[appKey];
                                                    const last30 = points.reduce((t, pt) => t + (pt.downloads || 0), 0);
                                                    const allTime = p.data.allTimeTotal?.[appKey] || 0;
                                                    return (
                                                        <div key={appKey} style={{ marginBottom: 14 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 11.5, opacity: 0.7, marginBottom: 4 }}>
                                                                <span>{appKey === 'customer' ? 'SnapWiz' : 'SnapMaster'}</span>
                                                                <span>
                                                                    <b style={{ opacity: 1 }}>{fmtNum(allTime)}</b> all-time · {fmtNum(last30)} last 30 days
                                                                </span>
                                                            </div>
                                                            <TrendChart
                                                                labels={points.map(pt => fmtDateLabel(pt.date))}
                                                                yFormat={fmtNum}
                                                                area
                                                                series={[{ name: 'Downloads', color: appKey === 'customer' ? ACCENT : ACCENT_2, values: points.map(pt => pt.downloads || 0) }]}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })() : null}


                        <div className="card" style={{ marginBottom: 16 }}>
                            <div className="card-body">
                                <h4 className="card-title">Photographer performance</h4>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                                    <div style={statTileStyle}><div style={statNumStyle}>{data.photographerPerformance.activeLast30}</div><div style={statLblStyle}>Active in last 30 days</div></div>
                                    <div style={statTileStyle}><div style={statNumStyle}>{data.photographerPerformance.newThisMonth}</div><div style={statLblStyle}>New this month</div></div>
                                    <div style={statTileStyle}><div style={statNumStyle}>{data.photographerPerformance.churnRatePercent}%</div><div style={statLblStyle}>Monthly churn rate</div></div>
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                    {data.photographerPerformance.topEarners.length === 0 ? (
                                        <p style={{ fontSize: 13, opacity: 0.6 }}>No completed shoots or payouts in the last 30 days.</p>
                                    ) : (
                                        <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={thStyle}>Photographer</th>
                                                    <th style={thStyle}>Zone</th>
                                                    <th style={thStyle}>Bookings (30d)</th>
                                                    <th style={thStyle}>Earnings (30d)</th>
                                                    <th style={thStyle}>Rating</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.photographerPerformance.topEarners.map(e => (
                                                    <tr key={e.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                                        <td style={{ padding: '10px 12px', fontWeight: 700 }}>{e.name}</td>
                                                        <td style={{ padding: '10px 12px' }}>{e.zone}</td>
                                                        <td style={{ padding: '10px 12px' }}>{e.bookings30d}</td>
                                                        <td style={{ padding: '10px 12px' }}>₹{e.earnings30d.toLocaleString('en-IN')}</td>
                                                        <td style={{ padding: '10px 12px' }}>{e.rating != null ? `★ ${e.rating.toFixed(1)}` : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: 16 }}>
                            <div className="card">
                                <div className="card-body">
                                    <div style={cardTitleRowStyle}>
                                        <h4 className="card-title" style={{ marginBottom: 0 }}>Customer growth</h4>
                                        <span style={crumbStyle}>new vs. returning customers · last 6 months</span>
                                    </div>
                                    <div style={{ fontSize: 11.5, marginBottom: 10 }}>
                                        <span style={legendDotStyle(ACCENT)} />Returning&nbsp;&nbsp;
                                        <span style={legendDotStyle(ACCENT_2)} />New
                                    </div>
                                    <GrowthBars months={data.customerGrowth} />
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%', justifyContent: 'center' }}>
                                    <div style={statTileStyle}><div style={statNumStyle}>₹{data.avgOrderValue.toLocaleString('en-IN')}</div><div style={statLblStyle}>Average order value</div></div>
                                    <div style={statTileStyle}><div style={statNumStyle}>{data.repeatBookingRatePercent}%</div><div style={statLblStyle}>Repeat booking rate</div></div>
                                </div>
                            </div>
                        </div>
                    </>
                    );
                })()}
                </div>
            </AdminShell>
        );
    }
}

const mapStateToProps = (state: any) => ({
    token: state.auth.token,
    role: state.auth.role,
});

export default connect(mapStateToProps)(AnalyticsPage);
